import { Component, forwardRef, Input, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbHighlight, NgbTypeahead, NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import { merge, Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from "rxjs/operators";
import { ITypeaheadPage, TypeaheadLoadMoreItem } from "./typeahead-input.models";

@Component({
	selector: "rl-typeahead-input",
	templateUrl: "./typeahead-input.component.html",
	styleUrls: ["./typeahead-input.component.scss"],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TypeaheadInputComponent),
			multi: true
		}
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgbTypeahead, NgbHighlight, FormsModule]
})
export class TypeaheadInputComponent<T> implements ControlValueAccessor, OnDestroy {
	@Input({ required: true }) searchFn: (term: string, page: number, pageSize: number) => Observable<ITypeaheadPage<T>>;
	@Input({ required: true }) labelFn: (item: T) => string;
	@Input({ required: true }) keyFn: (item: T) => string | number;
	@Input() pageSize = 10;
	@Input() debounceMs = 200;
	@Input() placeholder = "Search...";

	keyword = "";
	value: T | null = null;
	isLoading = false;
	isDisabled = false;

	onChange = (_: T | null) => { };
	onTouched = () => { };

	private _currentPage = 0;
	private _currentTerm = "";
	private _allResults: T[] = [];
	private _totalResults = 0;
	private _loadMore$ = new Subject<string>();

	writeValue(value: T | null): void {
		this.value = value;
		this.keyword = value ? this.labelFn(value) : "";
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}

	inputFormatter = (): string => this.keyword ?? "";

	typeAheadSearch = (text$: Observable<string>): Observable<(T | TypeaheadLoadMoreItem)[]> => {
		const newSearch$ = text$.pipe(
			debounceTime(this.debounceMs),
			distinctUntilChanged(),
			tap(term => {
				this._currentTerm = term;
				this._currentPage = 0;
				this._allResults = [];
			})
		);

		return merge(newSearch$, this._loadMore$).pipe(
			switchMap(term => this.fetchPage(term, this._currentPage))
		);
	};

	typeAheadSelected(event: NgbTypeaheadSelectItemEvent<T | TypeaheadLoadMoreItem>): void {
		this.value = event.item as T;
		this.keyword = this.labelFn(this.value);
		this.onChange(this.value);
		this.onTouched();
	}

	loadMore(event: Event) {
		event.preventDefault();
		event.stopPropagation();
		this._currentPage++;
		this._loadMore$.next(this._currentTerm);
	}

	clearValue(): void {
		this.value = null;
		this.keyword = "";
		this.onChange(null);
		this.onTouched();
	}

	isLoadMore(item: T | TypeaheadLoadMoreItem): item is TypeaheadLoadMoreItem {
		return (item as TypeaheadLoadMoreItem).__typeaheadLoadMore === true;
	}

	private fetchPage(term: string, page: number): Observable<(T | TypeaheadLoadMoreItem)[]> {
		this.isLoading = true;
		return this.searchFn(term, page, this.pageSize).pipe(
			tap(result => {
				this._totalResults = result.total;
				if (page === 0) {
					this._allResults = result.items;
				} else {
					this._allResults = [...this._allResults, ...result.items];
				}
			}),
			map(() => {
				const results: (T | TypeaheadLoadMoreItem)[] = [...this._allResults];
				if (this._allResults.length < this._totalResults) {
					results.push({
						__typeaheadLoadMore: true,
						loaded: this._allResults.length,
						total: this._totalResults
					});
				}
				return results;
			}),
			catchError(() => of([])),
			tap(() => {
				this.isLoading = false;
			})
		);
	}

	ngOnDestroy(): void {
		this._loadMore$.complete();
	}
}
